// FILE: textToSpeech.jsx
import React, { useState, useEffect, useRef } from 'react'
import { Repeat, PlayCircle } from 'phosphor-react'

const TextToSpeech = ({
    audioBuffers,
    transcripts,
    startTranscriptSelected,
}) => {
    // --- New State for Robust Playback ---
    const [segmentDurations, setSegmentDurations] = useState([]) // duration (seconds) for each segment
    const [totalDuration, setTotalDuration] = useState(0) // total duration (seconds)
    const [currentTime, setCurrentTime] = useState(0) // current time in the combined audio
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0)
    const [segmentStartTime, setSegmentStartTime] = useState(0) // start time of current segment in combined audio
    const [segmentAudio, setSegmentAudio] = useState(null) // Audio element for current segment
    const [seeking, setSeeking] = useState(false)
    const scrubBarRef = useRef(null)
    const segmentAudioRef = useRef(null)
    const durationsLoadedRef = useRef(false)
    const [autoResumeOnNewSegment, setAutoResumeOnNewSegment] = useState(false)
    const [durationsLoading, setDurationsLoading] = useState(false)
    const [isUserSeeking, setIsUserSeeking] = useState(false)

    // --- Utility: Load duration for a blob ---
    const getAudioDuration = blob => {
        return new Promise(resolve => {
            const audio = new window.Audio()
            audio.src = URL.createObjectURL(blob)
            audio.addEventListener('loadedmetadata', () => {
                const duration = audio.duration
                URL.revokeObjectURL(audio.src)
                resolve(duration)
            })
            audio.addEventListener('error', () => {
                resolve(0)
            })
        })
    }

    // --- Load durations for all segments whenever audioBuffers changes ---
    useEffect(() => {
        let isMounted = true
        const loadDurations = async () => {
            setDurationsLoading(true)
            const durations = await Promise.all(
                audioBuffers.map(blob => getAudioDuration(blob)),
            )
            if (isMounted) {
                setSegmentDurations(durations)
                setTotalDuration(durations.reduce((a, b) => a + b, 0))
                setDurationsLoading(false)
            }
        }
        if (audioBuffers.length > 0) {
            loadDurations()
        } else {
            setSegmentDurations([])
            setTotalDuration(0)
            setDurationsLoading(false)
        }
        return () => {
            isMounted = false
        }
    }, [audioBuffers])

    // --- Find segment index and offset for a given time ---
    const findSegmentForTime = (time, durations) => {
        let acc = 0
        for (let i = 0; i < durations.length; i++) {
            if (time < acc + durations[i]) {
                return { index: i, offset: time - acc }
            }
            acc += durations[i]
        }
        // If time is at or beyond end, return last segment
        return {
            index: durations.length - 1,
            offset: durations[durations.length - 1] || 0,
        }
    }

    // --- Play a segment from a given offset ---
    const playSegment = (index, offset = 0) => {
        if (!audioBuffers[index]) return
        if (segmentAudioRef.current) {
            segmentAudioRef.current.pause()
            segmentAudioRef.current.src = ''
        }
        const url = URL.createObjectURL(audioBuffers[index])
        const audio = new window.Audio(url)
        segmentAudioRef.current = audio
        audio.currentTime = offset
        audio.play()
        setIsPlaying(true)
        setCurrentSegmentIndex(index)
        setSegmentStartTime(
            segmentDurations.slice(0, index).reduce((a, b) => a + b, 0),
        )
        // --- On timeupdate, update currentTime ---
        audio.ontimeupdate = () => {
            if (!isUserSeeking) {
                setCurrentTime(segmentStartTime + audio.currentTime)
            }
        }
        // --- On ended, play next segment if available ---
        audio.onended = () => {
            URL.revokeObjectURL(url)
            if (index + 1 < audioBuffers.length) {
                playSegment(index + 1, 0)
            } else {
                setIsPlaying(false)
                setAutoResumeOnNewSegment(true)
            }
        }
        // --- On error, stop playback ---
        audio.onerror = () => {
            URL.revokeObjectURL(url)
            setIsPlaying(false)
        }
    }

    // --- Play from currentTime ---
    const handlePlay = () => {
        if (audioBuffers.length === 0 || segmentDurations.length === 0) return
        const { index, offset } = findSegmentForTime(
            currentTime,
            segmentDurations,
        )
        playSegment(index, offset)
    }

    // --- Pause playback ---
    const handlePause = () => {
        if (segmentAudioRef.current) {
            segmentAudioRef.current.pause()
        }
        setIsPlaying(false)
    }

    // --- Scrub bar seeking logic ---
    const handleSeekChange = e => {
        const newTime = parseFloat(e.target.value)
        setCurrentTime(newTime)
    }

    const handleSeekStart = () => {
        setIsUserSeeking(true)
    }

    const handleSeekEnd = e => {
        const newTime = parseFloat(e.target.value)
        setCurrentTime(newTime)
        setIsUserSeeking(false)
        if (isPlaying) {
            // If playing, jump to new segment/offset and play
            const { index, offset } = findSegmentForTime(
                newTime,
                segmentDurations,
            )
            playSegment(index, offset)
        } else {
            // If paused, just update current segment/offset
            const { index, offset } = findSegmentForTime(
                newTime,
                segmentDurations,
            )
            setCurrentSegmentIndex(index)
            setSegmentStartTime(
                segmentDurations.slice(0, index).reduce((a, b) => a + b, 0),
            )
            if (segmentAudioRef.current) {
                segmentAudioRef.current.pause()
                segmentAudioRef.current.src = ''
            }
        }
    }

    // --- When new segments are added, if playing, continue playback ---
    useEffect(() => {
        if (
            isPlaying &&
            segmentAudioRef.current &&
            segmentAudioRef.current.ended
        ) {
            // If playback ended but new segments arrived, resume
            if (currentSegmentIndex + 1 < audioBuffers.length) {
                playSegment(currentSegmentIndex + 1, 0)
            }
        }
        // If playback ended at the end, and new segments arrive, auto-resume
        if (
            autoResumeOnNewSegment &&
            !isPlaying &&
            currentSegmentIndex + 1 < audioBuffers.length
        ) {
            playSegment(currentSegmentIndex + 1, 0)
            setAutoResumeOnNewSegment(false)
        }
    }, [
        audioBuffers.length,
        isPlaying,
        autoResumeOnNewSegment,
        currentSegmentIndex,
    ])

    // --- Cleanup on unmount ---
    useEffect(() => {
        return () => {
            if (segmentAudioRef.current) {
                segmentAudioRef.current.pause()
                segmentAudioRef.current.src = ''
            }
        }
    }, [])

    // --- Format time for display ---
    const formatTime = t => {
        if (isNaN(t)) return '0:00'
        const m = Math.floor(t / 60)
        const s = Math.floor(t % 60)
        return `${m}:${s.toString().padStart(2, '0')}`
    }

    // --- Go Live: jump to last segment and play ---
    const handleGoLive = () => {
        if (audioBuffers.length === 0 || segmentDurations.length === 0) return
        const lastIndex = audioBuffers.length - 1
        const lastStart = segmentDurations
            .slice(0, lastIndex)
            .reduce((a, b) => a + b, 0)
        setCurrentTime(lastStart)
        playSegment(lastIndex, 0)
    }

    // --- Replay: jump to start and play ---
    const handleReplay = () => {
        if (audioBuffers.length === 0 || segmentDurations.length === 0) return
        setCurrentTime(0)
        playSegment(0, 0)
    }

    return (
        <div style={{ width: '100%', maxWidth: 600 }}>
            <div className="d-flex align-items-center mb-2">
                <button
                    className="btn btn-info me-2"
                    onClick={isPlaying ? handlePause : handlePlay}
                    disabled={audioBuffers.length === 0 || durationsLoading}
                >
                    {isPlaying ? (
                        <Repeat size={32} />
                    ) : (
                        <PlayCircle size={32} />
                    )}
                </button>
                <button
                    className="btn btn-success me-2"
                    onClick={handleGoLive}
                    disabled={audioBuffers.length === 0 || durationsLoading}
                    title="Go Live"
                >
                    Go Live
                </button>
                <button
                    className="btn btn-secondary me-2"
                    onClick={handleReplay}
                    disabled={audioBuffers.length === 0 || durationsLoading}
                    title="Replay"
                >
                    Replay
                </button>
                {/* <span style={{ minWidth: 50 }}>{formatTime(currentTime)}</span> */}
                {/* <input
                    ref={scrubBarRef}
                    type="range"
                    min={0}
                    max={totalDuration}
                    step={0.01}
                    value={currentTime}
                    onChange={handleSeekChange}
                    onMouseDown={handleSeekStart}
                    onMouseUp={handleSeekEnd}
                    onTouchStart={handleSeekStart}
                    onTouchEnd={handleSeekEnd}
                    style={{ flex: 1, margin: '0 10px' }}
                    disabled={audioBuffers.length === 0 || durationsLoading}
                /> */}
                {/* <span style={{ minWidth: 50 }}>
                    {formatTime(totalDuration)}
                </span> */}
            </div>
            <div style={{ fontSize: 14, color: '#555', minHeight: 24 }}>
                {durationsLoading && <span>Loading audio durations...</span>}
                {/* {transcripts && transcripts.length > 0 && !durationsLoading && (
                    <>
                        <b>Transcript:</b> {transcripts.join(' ')}
                    </>
                )} */}
            </div>
        </div>
    )
}

export default TextToSpeech
